import type { Messages } from "./index.js";
import { BRAND_NAME_FA, CONTACT_EMAIL } from "../brand.js";

/**
 * Persian copy. Declared AS `Messages`, so a missing key fails typecheck.
 * Register: warm, spoken Persian (the way a friend would explain the site),
 * not translationese. NATIVE-SPEAKER REVIEW REQUIRED before the fa site goes
 * live — especially anything a contributor consents against.
 *
 * \u2066 … \u2069 (LRI … PDI) wrap Latin runs such as LGBTQIA+ and the film
 * title. Without the isolate the trailing "+" is a neutral character, inherits
 * the paragraph's RTL direction and renders on the wrong side: "+LGBTQIA".
 * Written as escapes, not raw characters, so they stay visible when editing.
 */
export const fa: Messages = {
  meta: {
    siteName: BRAND_NAME_FA,
    siteNameFa: BRAND_NAME_FA,
    // The Persian name already means "house of stories", so no tagline.
    brandTagline: "",
    tagline: "داستان‌هایی که سفر می‌کنند.",
    logline:
      "قصه\u200cخانه از روایت‌های دست‌اولِ رفتن مستند می‌سازد. اولین فیلم ما \u2066My Child\u2069 است.",
    description:
      "قصه\u200cخانه یک گروه کوچک مستندساز است. اولین فیلم ما، \u2066My Child\u2069، چهار فرد \u2066LGBTQIA+\u2069 افغان را در تبعید در اروپا دنبال می‌کند. داستانت را به‌صورت خصوصی با ما در میان بگذار، کار را دنبال کن و به ساختِ فیلم بعدی کمک کن.",
    statusBadge: "اولین فیلم · \u2066My Child\u2069",
    provenance: "روایت‌های دست‌اولِ رفتن، به روایت کسانی که آن را زیسته‌اند.",
    pages: {
      home: "قصه\u200cخانه — مستندهایی به روایت خودِ آدم‌ها",
      film: "\u2066My Child\u2069 — مستندی از کنیز مرادی",
      share: "داستانت را بگو — قصه\u200cخانه",
      support: "از فیلم‌ها حمایت کن — قصه\u200cخانه",
      contact: "همراه شو — قصه\u200cخانه",
      clips: "کلیپ‌ها — قصه\u200cخانه",
      privacy: "حریم خصوصی — قصه\u200cخانه",
      terms: "شرایط ارسال — قصه\u200cخانه",
      withdraw: "داستان شما — قصه\u200cخانه",
      notFound: "صفحه پیدا نشد — قصه\u200cخانه",
    },
    descriptions: {
      share:
        "داستانت را بنویس، یا به‌صورت پیام صوتی، ویدئو یا لینک بفرست. به‌طور پیش‌فرض خصوصی، اگر بخواهی بی‌نام، و نگهداری‌شده در سوئد.",
      support: "هنوز پولی نمی‌گیریم. ایمیلت را بگذار تا اولین کسی باشی که خبردار می‌شود حمایت از فیلم ممکن شده است.",
      contact: "می‌خواهی در تولیدات آینده باشی، داستانی پیشنهاد دهی یا با تیم در تماس باشی؟ برایمان بنویس.",
      clips: "کلیپ‌های قصه\u200cخانه را ببین و به اشتراک بگذار.",
      film: "\u2066My Child\u2069 مستندی ۲۲ دقیقه‌ای از کنیز مرادی است. چهار فرد \u2066LGBTQIA+\u2069 افغان داستان بزرگ‌شدن، رفتن، و آنچه اروپا از آب درآمد را روایت می‌کنند.",
    },
  },

  nav: {
    film: "فیلم",
    share: "داستانت را بگو",
    about: "دربارهٔ فیلم",
    clips: "کلیپ‌ها",
    support: "حمایت",
    contact: "تماس",
    language: "زبان",
    switchTo: "English",
    skipToContent: "رفتن به محتوا",
    menu: "منو",
    close: "بستن",
  },

  hero: {
    kicker: "مستندهایی به روایت خودِ آدم‌ها",
    title: "هر داستانِ این‌جا راهی طولانی آمده است.",
    lede: "قصه\u200cخانه از آنچه آدم‌ها هنگام رفتن با خود می‌برند فیلم می‌سازد. اولین فیلم، \u2066My Child\u2069، چهار فرد \u2066LGBTQIA+\u2069 افغان است که داستان خودشان را به زبان خودشان می‌گویند. هنوز گوش‌مان با داستان بعدی است. داستان تو هم می‌تواند بخشی از آن باشد.",
    ctaShare: "داستانت را بگو",
    ctaFilm: "تریلر را ببین",
    ctaUpdates: "خبرها را دنبال کن",
    emailLabel: "ایمیل شما",
    emailPlaceholder: "you@example.com",
    emailButton: "اولین کسی باش که تریلر را می‌بیند",
    emailHint: "فقط یک فیلد، بدون نام. اول یک ایمیل تأیید می‌فرستیم؛ هر وقت خواستی لغو اشتراک کن.",
    counter: "تا امروز {count} داستان به اشتراک گذاشته شده",
  },

  about: {
    heading: "دربارهٔ قصه\u200cخانه",
    p1: "قصه\u200cخانه با یک پرسش ساده شروع شد: زندگی وقتی میان دو جا تقسیم می‌شود چه شکلی پیدا می‌کند؟ پاسخ‌ها به‌ندرت ساده‌اند. تماس‌های تلفنی در ساعت‌های عجیب، استکانی که از سه بار جابه‌جایی جان به در برده، زبانی که بچه‌ها با لهجه حرف می‌زنند.",
    p2: "هر بار یک فیلم می‌سازیم، آهسته، و تماماً از روایت‌های دست‌اول. اولی \u2066My Child\u2069 است: چهار فرد \u2066LGBTQIA+\u2069 افغان که افغانستان را ترک کرده‌اند و حالا در آلمان و بلژیک زندگی می‌کنند، بیست‌ودو دقیقه رو‌به‌روی دوربین. فیلم بعدی هنوز ساخته نشده است. برای همین هنوز گوش می‌دهیم.",
    p3: "هر چه با ما در میان بگذارید، تا وقتی خودتان جور دیگری نخواهید، فقط نزد تیم کوچک ما می‌ماند. این متن ریز پای قرارداد نیست؛ اصلِ کار همین است.",
    directorNoteHeading: "یادداشتی از کنیز",
    directorNote:
      "ما تیم کوچکی هستیم که این فیلم‌ها را آهسته و با دقت می‌سازیم. من هر داستانی که می‌رسد را می‌خوانم و می‌شنوم. شاید نتوانیم به همه پاسخ بدهیم، اما هر یک از آن‌ها به فیلم شکل می‌دهد.",
    howWeWorkHeading: "چطور کار می‌کنیم",
    howWeWork:
      "فیلم‌برداری بخش کوتاه کار است. تدوین، ترجمه، زیرنویس و میکس صدا خیلی بیشتر طول می‌کشد، و ما به‌جای عجله، وقت می‌گذاریم. در همین فاصله داستان‌ها را جمع می‌کنیم، هم برای فیلمی که در دست است و هم برای فیلم بعدی.",
    cta: "داستانت را بگو",
  },

  trailer: {
    heading: "تریلر",
    comingTitle: "تریلر در راه است",
    comingBody: "اول به شما خبر می‌دهیم.",
    play: "پخش تریلر",
    playClip: "پخش کلیپ",
    loadsFromYouTube: "با پخش، ویدئو از یوتیوب بارگذاری می‌شود.",
    warning: "هشدار محتوا: این تریلر به خشونت جنسی علیه یک کودک و به خودکشی اشاره می‌کند.",
  },

  film: {
    kicker: "اولین فیلم ما",
    title: "\u2066My Child\u2069",
    byline: "مستندی از کنیز مرادی",
    runtime: "۲۲ دقیقه",
    runtimeLabel: "مدت",
    languageLabel: "زبان",
    language: "دری، با زیرنویس انگلیسی",
    directorLabel: "کارگردان",
    director: "کنیز مرادی",

    logline:
      "چهار فرد \u2066LGBTQIA+\u2069 افغان داستان بزرگ‌شدن، رفتن، و آنچه اروپا از آب درآمد را روایت می‌کنند.",

    posters: {
      portrait: "پوستر: نجیب فیضی با پیراهن زرد روبه‌روی پس‌زمینهٔ روشن استودیو نشسته و یک دستش را کنار صورتش گرفته است.",
      object: "پوستر: یک گل روشن در برابر سیاهیِ نزدیک به مطلق.",
      dance: "پوستر: سیلک با پیراهن سنتی افغان به رنگ نیلی و نقره‌ای می‌رقصد.",
    },

    synopsisHeading: "این فیلم چیست",
    synopsis1:
      "نجیب، نورا، دیبا و سیلک در افغانستان بزرگ شدند، جایی که مدام به آن‌ها می‌گفتند چه هستند. هر چهار نفر رفتند. \u2066My Child\u2069 بیست‌ودو دقیقه است از این چهار نفر، رو‌به‌روی دوربین، دربارهٔ کودکی‌ای که از سر گذراندند، راهی که بیرون‌شان آورد، و آنچه در مقصد پیدا کردند.",
    synopsis2:
      "این قصهٔ نجات نیست. اروپا هم جایی است که قانون از آن‌ها محافظت می‌کند و هم جایی که همسایه آب دهان می‌اندازد، جایی که آپارتمانِ شش‌ساله هنوز بیشتر شبیه هتل است تا خانه. با این همه، فیلم روی چیزی تمام می‌شود که هر کدام به‌سویش می‌روند: مدرک پرستاری، یک سالن زیبایی، یک بیمارستان کودکان، عروسی‌ای برای خواندن.",

    titleHeading: "نام فیلم از کجا می‌آید",
    titleOrigin:
      "مردی در مسیر پناه‌جویی به کودکی دوازده‌ساله سرپناهِ یک شب را تعارف کرد و او را «بچه‌ام» صدا زد، که در دری از مهربان‌ترین چیزهایی است که یک بزرگ‌سال می‌تواند به کودکی بگوید. آنچه بعد از آن کرد، دلیل وجود این فیلم است. تا پایان این بیست‌ودو دقیقه، این عبارت به کسانی برمی‌گردد که از آن‌ها گرفته شده بود.",

    peopleHeading: "آدم‌های فیلم",
    peopleNote:
      "هر چهار نفر آشکارا از هویت خود گفته‌اند و در فیلم هم همین را می‌گویند. نام‌ها این‌جا دقیقاً همان‌طور آمده که فیلم آورده است.",

    warningHeading: "پیش از تماشا",
    warning:
      "\u2066My Child\u2069 شامل روایت‌های دست‌اول از کودک‌آزاری جنسی و تجاوز، آزار جنسی از سوی معلم، خشونت والدین، و خودکشی در جامعهٔ \u2066LGBTQIA+\u2069 افغان است. کسانی که این داستان‌ها را می‌گویند، خودشان خواسته‌اند که بگویند. خودت تصمیم بگیر که حالا وقتش هست یا نه.",

    trailerHeading: "تریلر",
    trailerComing:
      "تریلر به‌زودی منتشر می‌شود. ایمیلت را بگذار تا همان روز خبرت کنیم.",

    screeningsHeading: "نمایش و مطبوعات",
    screenings:
      "برای برنامه‌ریزی جشنواره، نسخهٔ مطبوعاتی یا نمایش در جمعِ محلی، برایمان بنویس و بگو کدام‌یک.",
    screeningsCta: "تماس بگیر",

    backHeading: "این فیلم از داستان‌هایی مثل داستان تو ساخته شده",
    backBody:
      "\u2066My Child\u2069 هست چون چهار نفر تصمیم گرفتند بلند بگویند. فیلم بعدی هم به همین نیاز دارد.",
  },

  people: {
    najib: {
      name: "نجیب فیضی",
      role: "درگ‌کوئین (آلمان)",
      note: "اولین افغانی که در شبکه‌های اجتماعی خود را \u2066LGBTQIA+\u2069 معرفی کرد، و در جامعه به «مادرِ \u2066LGBTQIA+\u2069» شناخته می‌شود. حالا درس پرستاری می‌خواند.",
    },
    nora: {
      name: "نورا فرش",
      role: "زن ترنس (آلمان)",
      note: "اولین زن ترنسی که در جامعهٔ افغان آشکارا از خود گفت.",
    },
    diba: {
      name: "دیبا مشرقی",
      role: "زن ترنس (آلمان)",
      note: "در آلمان با افراد \u2066LGBTQIA+\u2069 کار می‌کند و هم‌زمان درس می‌خواند.",
    },
    silk: {
      name: "سیلک",
      role: "زن ترنس (بلژیک)",
      note: "«خیلی‌ها صدای ما را نشنیده‌اند. حالا با صدای این زنگوله‌ها در دستم، خواهند شنید.»",
    },
  },

  storyBlock: {
    heading: "داستانت را بگو",
    invitation: "هر کسی داستانی دارد. داستان تو می‌تواند به فیلم بعدی شکل بدهد.",
    body: "می‌توانی آن را بنویسی، به‌صورت پیام صوتی ضبط کنی، ویدئوی کوتاهی بفرستی، یا لینکی بگذاری. لازم نیست بگویی که هستی.",
    promptsHeading: "اگر نمی‌دانی از کجا شروع کنی",
    cta: "شروع",
    private: "به‌طور پیش‌فرض خصوصی",
    anonymous: "اگر بخواهی، بی‌نام",
    sweden: "نگهداری در سوئد",
  },

  share: {
    heading: "داستانت را بگو",
    stepOf: "مرحلهٔ {n} از ۳",
    trustHeading: "داستان تو به کجا می‌رود",
    trust: {
      private:
        "به‌طور پیش‌فرض خصوصی. داستانت فقط به تیم کوچک ما می‌رسد. بدون اجازهٔ جداگانه و صریح تو هرگز منتشر یا عمومی نمی‌شود.",
      sweden:
        "نگهداری در سوئد. همه‌چیز روی سرور خودمان در اتحادیهٔ اروپاست. نه گوگل، نه متا، نه ابزار تحلیلی در این صفحه.",
      identity:
        "خودت انتخاب می‌کنی که کیستی. نام واقعی، نام مستعار، یا کاملاً بی‌نام — و تا پیش از هر انتشاری می‌توانی این انتخاب را تغییر دهی.",
      withdraw:
        "می‌توانی پس بگیری. هر داستان یک پیوند خصوصی دارد برای پس‌گرفتن یا تغییر ترجیحاتت. نه ایمیل لازم است، نه حساب کاربری.",
    },
    commitmentsHeading: "تعهدهای ما",
    commitments: {
      contactFirst: "اگر روزی بخواهیم داستانت را عمومی کنیم، اگر راهی برای تماس داده باشی، اول با تو تماس می‌گیریم.",
      review: "مشارکت‌کنندگانِ قابل‌شناسایی و در معرض خطر می‌توانند پیش از نهایی‌شدن فیلم، بخش خود را ببینند.",
      blur: "محو کردن چهره و تغییر صدا در دسترس است.",
      redact: "پیش از هر انتشاری، جزئیاتی که می‌تواند اعضای خانواده را شناسایی کند حذف می‌کنیم.",
      metadata: "اطلاعات مکان و دستگاه از هر عکس، ویدئو و پیام صوتی همان لحظهٔ رسیدن پاک می‌شود.",
    },
    noAccount: "بدون ثبت‌نام. بدون حساب کاربری. فقط داستان تو.",
    readBy: "کنیز و تیم کوچک قصه\u200cخانه داستان‌ها را می‌خوانند و می‌شنوند، نه یک صندوق ایمیل.",
    expectation: "همه را می‌خوانیم و می‌شنویم. شاید نتوانیم به هر داستانی پاسخ بدهیم.",

    step1Heading: "دوست داری چطور بگویی؟",
    formats: {
      text: { title: "بنویس", body: "داستانت را تایپ کن. هر چقدر که بخواهی، بلند یا کوتاه." },
      audio: { title: "پیام صوتی ضبط کن", body: "همین‌جا ضبط کن، یا فایلی که داری را بفرست. تا ۱۰ دقیقه." },
      video: { title: "ویدئو ضبط یا آپلود کن", body: "تا ۳۰۰ مگابایت و ۱۵ دقیقه. آپلود می‌تواند متوقف و ادامه پیدا کند." },
      link: { title: "لینک بفرست", body: "لینک چیزی را که قبلاً منتشر کرده‌ای بگذار." },
    },

    step2Heading: "داستان تو",
    promptsHint: "روی یک پرسش بزن تا نقطهٔ شروع باشد، یا کلاً از آن‌ها بگذر.",
    titleLabel: "عنوان (اختیاری)",
    textLabel: "داستان تو",
    textPlaceholder: "از هر جا شروع کن. از وسط هم خوب است.",
    charCount: "{count} / {max}",
    noteLabel: "چیزی هست که بخواهی اضافه کنی؟ (اختیاری)",
    linkLabel: "لینک",
    linkPlaceholder: "https://",
    linkWarning: "این لینک به حساب عمومی خودت اشاره دارد. گذاشتنش این‌جا آن را خصوصی نمی‌کند.",
    linkHelp: "یوتیوب، اینستاگرام، تیک‌تاک، تلگرام، وبلاگ — هر جای عمومی.",

    recorder: {
      start: "شروع ضبط",
      stop: "توقف",
      recording: "در حال ضبط",
      rerecord: "دوباره ضبط کن",
      use: "از این ضبط استفاده کن",
      preview: "گوش بده",
      elapsed: "زمان گذشته",
      remaining: "{time} باقی مانده",
      permission: "مرورگر برای استفاده از میکروفون اجازه می‌خواهد.",
      unsupported: "ضبط در این مرورگر ممکن نیست. می‌توانی به‌جای آن فایل آپلود کنی.",
      orUpload: "یا یک فایل صوتی آپلود کن",
      denied: "دسترسی به میکروفون داده نشد. هنوز می‌توانی فایل آپلود کنی.",
    },
    uploader: {
      choose: "یک ویدئو انتخاب کن",
      dropHint: "یا این‌جا رها کن",
      uploading: "در حال آپلود",
      paused: "متوقف",
      pause: "توقف",
      resume: "ادامه",
      cancel: "لغو",
      done: "آپلود شد",
      processing: "در حال بررسی فایل",
      progress: "{percent}٪ آپلود شده",
      tooLarge: "این فایل از حد {max} بزرگ‌تر است.",
      tooLong: "این ضبط از {max} طولانی‌تر است.",
      rejected: "نتوانستیم این فایل را بخوانیم. قالب دیگری یا کلیپ کوتاه‌تری را امتحان کن.",
      stuck: `این بیش از حد انتظار طول کشیده. می‌توانی منتظر بمانی، یا فایل را به ${CONTACT_EMAIL} ایمیل کنی.`,
      failed: "آپلود ناموفق بود. متن تو در این صفحه ذخیره شده؛ هر وقت آماده بودی دوباره تلاش کن.",
    },

    step3Heading: "تو کیستی، و چه اجازه‌ای می‌دهی",
    identityLabel: "اگر داستانم روزی عمومی شد، نامم این‌طور بیاید",
    identity: {
      real_name: "نام واقعی‌ام",
      pseudonym: "یک نام مستعار",
      anonymous: "کاملاً بی‌نام",
    },
    identityHint: "تا پیش از هر انتشاری می‌توانی این را تغییر دهی.",
    nameLabel: "نام",
    pseudonymLabel: "نام مستعار",
    emailLabel: "ایمیل (اختیاری)",
    emailHint: "فقط اگر بخواهی بتوانیم با تو تماس بگیریم.",
    emailRequiredHint: "برای گزینه‌های تماسی که تیک زده‌ای لازم است.",
    under18: `زیر ۱۸ سال هستی؟ به ${CONTACT_EMAIL} ایمیل بزن — به رضایت یکی از والدین یا سرپرست نیاز داریم.`,
    consentHeading: "رضایت تو",
    consentIntro: "دو گزینه الزامی است. باقی واقعاً اختیاری‌اند: داستانت بدون آن‌ها هم پذیرفته می‌شود.",
    required: "الزامی",
    optional: "اختیاری",
    readFull: "متن کامل را بخوان",
    hideFull: "متن کامل را ببند",
    submit: "داستانم را بفرست",
    sending: "در حال ارسال",
    back: "برگرد",
    next: "ادامه",

    done: {
      heading: "سپاسگزاریم.",
      body: "داستانت رسید. اکنون فقط نزد تیم کوچک ما است.",
      linkHeading: "این پیوند را نگه دار",
      linkBody:
        "با آن می‌توانی داستانت را پس بگیری یا نحوهٔ ذکر نامت را تغییر دهی — حتی اگر ایمیلی به ما نداده باشی. اگر گم شود، نمی‌توانیم آن را برایت بازیابی کنیم.",
      copy: "کپی پیوند",
      copied: "کپی شد",
      emailed: "آن را برایت ایمیل هم کردیم.",
      notEmailed:
        "نتوانستیم آن را به ایمیلت بفرستیم. پیوند بالا تنها نسخهٔ موجود است؛ لطفاً پیش از بستن این صفحه آن را ذخیره کن.",
      expectation: "همه را می‌خوانیم و می‌شنویم. شاید نتوانیم به هر داستانی پاسخ بدهیم.",
      another: "داستان دیگری بگو",
    },
    errors: {
      generic: "مشکلی پیش آمد. متن تو هنوز این‌جاست — لطفاً دوباره تلاش کن.",
      tooFast: "خیلی سریع بود. لطفاً لحظه‌ای صبر کن و دوباره تلاش کن.",
      rateLimited: "ارسال‌های زیادی از این اتصال انجام شده. لطفاً یک ساعت دیگر تلاش کن.",
      invalid: "لطفاً فیلدهای مشخص‌شده را بررسی کن.",
      storyRequired: "لطفاً دست‌کم چند جمله بنویس.",
      linkRequired: "لطفاً لینکی بگذار که با https:// شروع شود.",
      mediaRequired: "لطفاً اول ضبط یا آپلود کن.",
      nameRequired: "لطفاً نامی وارد کن، یا بی‌نام را انتخاب کن.",
      emailRequired: "لطفاً برای گزینه‌هایی که تیک زده‌ای ایمیل بگذار.",
      emailInvalid: "این نشانی ایمیل درست به نظر نمی‌رسد.",
      consentRequired: "دو گزینهٔ الزامی باید تیک زده شوند.",
    },
  },

  withdraw: {
    heading: "داستان شما",
    body: "این پیوند خصوصی، یکی از داستان‌هایی را که با قصه\u200cخانه در میان گذاشته‌ای کنترل می‌کند.",
    statusLabel: "وضعیت",
    identityHeading: "نحوهٔ ذکر نام تو",
    save: "ذخیره",
    saved: "ذخیره شد",
    withdrawHeading: "پس‌گرفتن داستان",
    withdrawBody:
      "پس‌گرفتن، داستانت، فایل‌هایی که آپلود کرده‌ای و مشخصاتت را از سیستم‌های ما حذف می‌کند. این کار قابل بازگشت نیست.",
    withdrawButton: "پس بگیر و حذف کن",
    confirm: "بله، داستانم را حذف کن",
    cancel: "نگهش دار",
    withdrawn: "داستانت حذف شد.",
    invalid: "این پیوند معتبر نیست، یا داستانی که به آن اشاره داشت قبلاً حذف شده است.",
    usedNotice:
      `این داستان در یک تولید استفاده شده است. همهٔ استفاده‌های تازه را متوقف کرده‌ایم؛ نسخه‌های منتشرشده قابل بازگرداندن نیست. سابقهٔ رضایت تو آن‌طور که برای فیلم لازم است نگه داشته می‌شود. برای هر پرسشی به ${CONTACT_EMAIL} ایمیل بزن.`,
  },

  support: {
    heading: "کمک کن این فیلم‌ها ساخته شوند",
    honest: "هنوز پولی نمی‌گیریم.",
    body: "ساختن یک مستند هزینهٔ واقعی دارد: تدوین، میکس صدا، زیرنویس به دو زبان، مجوزها. وقتی امکان حمایت را باز کنیم، اول به کسانی که در این فهرست‌اند خبر می‌دهیم.",
    fundsHeading: "حمایت شما به چه چیزی می‌رسد",
    funds: {
      edit: "تدوین نهایی و تصحیح رنگ",
      sound: "میکس صدا",
      subtitles: "زیرنویس انگلیسی و فارسی",
      licensing: "مجوز موسیقی و آرشیو",
    },
    formHeading: "خبرم کن",
    emailLabel: "ایمیل",
    nameLabel: "نام (اختیاری)",
    helpLabel: "دوست داری چطور کمک کنی؟",
    help: {
      funding: "وقتی حمایت مالی باز شد، کمک کنم",
      screening: "یک نمایش جمعی برگزار کنم",
      connect: "شما را به کسی وصل کنم که باید این را ببیند",
      volunteer: "مهارت‌هایم را داوطلبانه بدهم — زیرنویس، ترجمه، اطلاع‌رسانی، طراحی",
    },
    amountLabel: "اگر کمک کنی، حدوداً چقدر؟ (اختیاری)",
    amounts: {
      lt25: "کمتر از ۲۵ یورو",
      "25_100": "۲۵ تا ۱۰۰ یورو",
      "100_500": "۱۰۰ تا ۵۰۰ یورو",
      gt500: "۵۰۰ یورو یا بیشتر",
      undisclosed: "ترجیح می‌دهم نگویم",
    },
    messageLabel: "چیز دیگری هست؟ (اختیاری)",
    consentContact: "وقتی حمایت از فیلم ممکن شد، با من تماس بگیرید.",
    consentUpdates: "گاه‌به‌گاه خبرهای فیلم را هم برایم بفرستید.",
    submit: "خبرم کن",
    done: "در فهرست هستی. یک ایمیل تأیید فرستادیم — روی دکمهٔ داخل آن بزن و کار تمام است.",
    early: "اولین کسی خواهی بود که خبردار می‌شود حمایت ممکن شده است.",
  },

  contact: {
    heading: "همراه شو",
    intro: "تیم کوچکی همه‌چیز را می‌خواند. پاسخ‌دادن ممکن است زمان ببرد.",
    topicLabel: "موضوع چیست؟",
    topics: {
      casting: "می‌خواهم در تولیدات آینده بازی کنم یا حاضر باشم",
      participation: "می‌خواهم کسی یا سرنخ داستانی را پیشنهاد دهم",
      press: "مطبوعات و نمایش‌ها",
      other: "چیز دیگری",
    },
    nameLabel: "نام",
    emailLabel: "ایمیل",
    phoneLabel: "تلفن (اختیاری)",
    languagesLabel: "زبان‌هایی که برای اجرا با آن‌ها راحتی",
    languages: { fa: "فارسی", en: "انگلیسی", sv: "سوئدی", other: "دیگر" },
    languagesOtherLabel: "کدام؟",
    locationLabel: "کجا زندگی می‌کنی؟ (اختیاری)",
    linksLabel: "لینک‌ها — شوریل، نمونه‌کار، شبکه‌های اجتماعی (اختیاری، تا ۳ لینک)",
    addLink: "لینک دیگری اضافه کن",
    ageConfirm: "من ۱۸ سال یا بیشتر دارم.",
    messageLabel: "پیام تو",
    consentStore: "قصه\u200cخانه می‌تواند این پیام را نگه دارد و درباره‌اش با من تماس بگیرد.",
    keepOnFile:
      "مشخصاتم را برای فراخوان‌های بازیگری آینده نگه دارید. با درخواست حذف می‌شود، و پس از ۲۴ ماه بی‌فعالیتی به‌طور خودکار.",
    submit: "بفرست",
    done: "سپاسگزاریم. پیامت رسید؛ هر وقت بتوانیم با تو تماس می‌گیریم.",
  },

  clips: {
    heading: "کلیپ‌ها",
    empty: "کلیپ‌های فیلم‌های ما به‌تدریج که منتشر می‌کنیم، این‌جا می‌آیند.",
    emptyCta: "خبرم کن",
    watch: "ببین",
    more: "کلیپ‌های بیشتر",
    backToFilm: "این فیلم از داستان‌هایی مثل داستان تو ساخته می‌شود.",
    shareCta: "داستانت را بگو",
  },

  shareBar: {
    heading: "این کلیپ را به اشتراک بگذار",
    captionLabel: "متن پست",
    captionHint: "اگر خواستی ویرایشش کن — نقطهٔ شروع است.",
    copyCaption: "کپی متن",
    captionCopied: "متن کپی شد. در {app} بچسبان.",
    copyLink: "کپی لینک",
    linkCopied: "لینک کپی شد",
    native: "اشتراک‌گذاری…",
    platforms: {
      telegram: "تلگرام",
      whatsapp: "واتساپ",
      instagram_copy: "اینستاگرام",
      tiktok_copy: "تیک‌تاک",
      x: "X",
      facebook: "فیسبوک",
      bluesky: "بلواسکای",
      linkedin: "لینکدین",
      reddit: "ردیت",
    },
  },

  newsletter: {
    confirmHeading: "فقط یک کلیک دیگر",
    confirmBody: "تأیید کن که می‌خواهی خبرهای قصه\u200cخانه را بگیری.",
    confirmButton: "بله، خبرم کن",
    confirmed: "تأیید شد. سپاسگزاریم — هر وقت خبری بود، با تو در تماس خواهیم بود.",
    invalid: "این پیوند تأیید دیگر معتبر نیست. می‌توانی دوباره در صفحهٔ اصلی ثبت‌نام کنی.",
    unsubscribeHeading: "لغو اشتراک",
    unsubscribeButton: "اشتراکم را لغو کن",
    unsubscribed: "اشتراکت لغو شد. از رفتنت متأسفیم.",
    checkInbox: "صندوق ایمیلت را ببین: یک ایمیل تأیید فرستادیم.",
  },

  footer: {
    dataNote: "میزبانی در سوئد. داستان‌ها خصوصی می‌مانند.",
    privacy: "حریم خصوصی",
    terms: "شرایط ارسال",
    contact: "تماس",
    email: CONTACT_EMAIL,
    rights: "© {year} قصه\u200cخانه",
  },

  common: {
    loading: "در حال بارگذاری",
    error: "مشکلی پیش آمد.",
    retry: "دوباره تلاش کن",
    required: "الزامی",
    optional: "اختیاری",
    yes: "بله",
    no: "نه",
    notFoundHeading: "صفحه پیدا نشد",
    notFoundBody: "این‌جا چیزی نیست. فیلم جای دیگری است.",
    home: "برگرد به آغاز",
    minutes: "دقیقه",
    mb: "مگابایت",
  },

  admin: {
    title: "قصه\u200cخانه — صف بررسی",
    login: "ورود",
    password: "گذرواژه",
    signOut: "خروج",
    queue: "داستان‌ها",
    contacts: "پیام‌ها",
    support: "حامیان",
    subscribers: "مشترکان",
    clips: "کلیپ‌ها",
    quarantine: "قرنطینه",
    status: "وضعیت",
    colStory: "داستان",
    colIdentity: "ذکر نام",
    colConsent: "رضایت",
    colReceived: "دریافت",
    colKind: "قالب",
    colPrompt: "پرسش استفاده‌شده",
    colEmail: "ایمیل",
    statuses: {
      new: "تازه",
      reviewed: "بررسی‌شده",
      shortlisted: "در فهرست کوتاه",
      contacted: "تماس گرفته شد",
      used: "استفاده‌شده",
      not_used: "استفاده‌نشده",
    },
    anonymous: "بی‌نام",
    pseudonym: "نام مستعار",
    realName: "نام واقعی",
    consentFilm: "مجوز فیلم",
    consentWeb: "انتشار بخش‌ها در وب",
    consentContact: "اجازهٔ تماس",
    granted: "داده شده",
    notGranted: "داده نشده",
    note: "یادداشت‌ها",
    delete: "حذف دائمی",
    deleteConfirm: "برای حذف کامل این داستان، فایل‌ها و مشخصاتش، DELETE را تایپ کن.",
    deleteRefusedUsed: "این داستان به‌عنوان استفاده‌شده در یک تولید علامت‌گذاری شده؛ سابقهٔ رضایت آن باید نگه داشته شود. فقط با دلیل قانونی از force استفاده کن.",
    spamFlag: "توسط هانی‌پات علامت‌گذاری شده — پیش از اعتماد بررسی کن",
    unflag: "هرزنامه نیست",
    empty: "هنوز چیزی این‌جا نیست.",
  },
};
